// App Bridge is only valid when the app is actually embedded in Shopify Admin.
// The meta tag + CDN script are injected at runtime instead of being static in
// index.html, so a top-level (non-embedded) load never pulls them in.

// True when embedded in the Shopify Admin iframe. The connectionComplete
// clause is a legacy fallback: Connecting.jsx now redirects straight into the
// embedded admin app URL after OAuth succeeds (replicating the old App Bridge
// v3 forceRedirect), so this param is no longer set by current code. Must
// mirror App.jsx's render condition exactly, since anything touching
// window.shopify (api.jsx, account.jsx) only runs on the paths this returns
// true for.
export function isAppBridgeContext(query = {}) {
    return window.top !== window.self || !!query.connectionComplete;
}

// Memoized so repeated calls (e.g. React 18 Strict Mode double-invocation)
// never inject duplicate <meta>/<script> tags and always share one promise.
let appBridgePromise = null;

// Injects the shopify-api-key meta tag and app-bridge.js CDN script, then
// resolves once window.shopify is available, or rejects if the script fails
// to load (rendering the app without window.shopify would crash every
// idToken() call). Callers must await this before rendering anything that
// reads window.shopify, and must handle rejection.
export function loadAppBridge(apiKey) {
    if (appBridgePromise) {
        return appBridgePromise;
    }

    appBridgePromise = new Promise((resolve, reject) => {
        const meta = document.createElement('meta');
        meta.name = 'shopify-api-key';
        meta.content = apiKey;
        document.head.appendChild(meta);

        const script = document.createElement('script');
        // Dynamically created scripts default to async=true, and app-bridge.js
        // self-validates and aborts if async/defer are set. Force it off.
        script.async = false;

        // NOTE: loaded without a Subresource Integrity hash. Shopify requires
        // apps to load app-bridge.js unversioned from this CDN and updates it
        // frequently, so a pinned SRI hash would break on every release. The
        // accepted residual risk is a compromise of cdn.shopify.com itself.
        script.src = 'https://cdn.shopify.com/shopifycloud/app-bridge.js';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Shopify App Bridge script from CDN'));
        document.head.appendChild(script);
    });

    return appBridgePromise;
}
